import React from 'react';
import { Modal, Select, Button, Tag, Empty, Alert } from 'antd';
import { TeamOutlined, InfoCircleOutlined } from '@ant-design/icons';
import HwzComponent from '../../../common/component/HwzComponent';
import Lang from '../../../common/model/global/Lang';
import MessageBoxUtil from '../../../common/util/MessageBoxUtil';
import Space from '../../../common/model/space/Space';
import Pager from '../../../common/model/base/Pager';
import Moon from '../../../common/model/global/Moon';
import { SpaceMemberRole, getSpaceMemberRoleMap } from '../../../common/model/space/member/SpaceMemberRole';

const { Option } = Select;

interface IProps {
  visible: boolean;
  onClose: () => void;
  onForward: (targetSpaceUuids: string[]) => void;
  matterUuids: string[];
  spaceUuid: string;
}

interface IState {
  selectedSpaceUuids: string[];
  spacePager: Pager<Space>;
  loading: boolean;
  forwarding: boolean;
  selectedSpaces: Space[];
  spaceRoles: Record<string, SpaceMemberRole>;
  error: string | null;
  spaceMemberRoleMap: ReturnType<typeof getSpaceMemberRoleMap>;
}

export default class ForwardDialogModal extends HwzComponent<IProps, IState> {
  user = Moon.getSingleton().user;

  constructor(props: IProps) {
    super(props);
    
    // 初始化时获取角色映射
    const spaceMemberRoleMap = getSpaceMemberRoleMap();
    
    this.state = {
      selectedSpaceUuids: [],
      spacePager: new Pager<Space>(this, Space, 50),
      loading: false,
      forwarding: false,
      selectedSpaces: [],
      spaceRoles: {},
      error: null,
      spaceMemberRoleMap // 存储角色映射
    };
  }

  componentDidMount() {
    this.refreshSpaceList();
      // 添加全局错误监听
    window.addEventListener('unhandledrejection', (event) => {
      console.log('未处理的 Promise 拒绝:', event.reason);
    });
    
    // 重写 console.error 来捕获错误
    const originalConsoleError = console.error;
    console.error = (...args) => {
      console.log('console.error 被调用:', args);
      originalConsoleError.apply(console, args);
    };
  }

  componentWillReceiveProps(nextProps: IProps) {
    if (nextProps.visible && !this.props.visible) {
      this.setState({
        selectedSpaceUuids: [],
        selectedSpaces: [],
        spaceRoles: {},
        error: null
      }, () => {
        this.refreshSpaceList();
      });
    }
  }

  componentWillUnmount() {
    // 清理事件监听器
    window.removeEventListener('unhandledrejection', () => {});
  }

  // 处理后台返回的错误码 - 增强调试版本
  handleBackendError(error: unknown): string {
    console.log('handleBackendError 被调用，错误对象:', error);
    
    if (!error) {
      console.log('错误对象为空');
      return Lang.t('error.unknownError') || '未知错误';
    }

    // 类型检查
    if (typeof error === 'object' && error !== null) {
      const err = error as any;
      
      console.log('错误对象类型检查通过，开始解析:', {
        hasResponse: !!err.response,
        hasRequest: !!err.request,
        responseData: err.response?.data
      });
      
      if (err.response && err.response.data) {
        const responseData = err.response.data;
        const errorCode = responseData.code || 'UNKNOWN';
        
        console.log('从响应数据中提取错误码:', { errorCode, responseData });
        
        const message = this.getErrorMessageByCode(errorCode);
        console.log('获取到的错误消息:', message);
        
        return message;
      }
      
      if (err.request) {
        console.log('检测到网络错误');
        return Lang.t('error.networkError') || '网络连接失败，请检查网络设置';
      }
    }
    
    // 处理 Error 实例
    if (error instanceof Error) {
      console.log('检测到 Error 实例:', error.message);
      return Lang.t('error.clientError', error.message) || `客户端错误：${error.message}`;
    }
    
    console.log('未知错误类型，返回默认错误消息');
    return Lang.t('error.unknownError') || '未知错误';
  }

  // 从错误对象中提取错误码 - 增强调试
  getErrorCode(error: unknown): string | undefined {
    console.log('getErrorCode 被调用，错误对象:', error);
    
    if (typeof error === 'object' && error !== null) {
      const err = error as any;
      const errorCode = err.response?.data?.code;
      console.log('提取到的错误码:', errorCode);
      return errorCode;
    }
    
    console.log('无法提取错误码');
    return undefined;
  }

  // 根据错误码获取错误消息
  getErrorMessageByCode(errorCode: string): string {
    const errorMap: { [key: string]: string } = {
      'OK': Lang.t('operations.success') || '操作成功',
      'BAD_REQUEST': Lang.t('error.badRequest') || '请求参数错误',
      'NEED_SHARE_CODE': Lang.t('error.needShareCode') || '需要分享码',
      'SHARE_CODE_ERROR': Lang.t('error.shareCodeError') || '分享码错误',
      'LOGIN': Lang.t('error.notLogin') || '未登录',
      'USER_DISABLED': Lang.t('error.userDisabled') || '用户已被禁用',
      'UNAUTHORIZED': Lang.t('error.unauthorized') || '未授权访问',
      'NOT_FOUND': Lang.t('error.notFound') || '资源不存在',
      'METHOD_NOT_ALLOWED': Lang.t('error.methodNotAllowed') || '请求方法不允许',
      'CONFLICT': Lang.t('error.conflict') || '资源冲突',
      'PRECONDITION_FAILED': Lang.t('error.preconditionFailed') || '前提条件失败',
      'UNSUPPORTED_MEDIA_TYPE': Lang.t('error.unsupportedMediaType') || '不支持的媒体类型',
      'RANGE_NOT_SATISFIABLE': Lang.t('error.rangeNotSatisfiable') || '请求范围无法满足',
      'NOT_INSTALLED': Lang.t('error.notInstalled') || '应用未安装',
      'SERVER': Lang.t('error.serverError') || '服务器内部错误',
      'UNKNOWN': Lang.t('error.unknownError') || '服务器未知错误'
    };

    return errorMap[errorCode] || Lang.t('error.unknownError') || '未知错误';
  }

  // 处理特殊错误（如未授权跳转登录）
  handleSpecialError(errorCode: string): void {
    switch (errorCode) {
      case 'UNAUTHORIZED':
      case 'LOGIN':
        this.handleUnauthorized();
        break;
      case 'USER_DISABLED':
        this.handleUserDisabled();
        break;
      case 'NOT_INSTALLED':
        this.handleNotInstalled();
        break;
    }
  }

  private handleUnauthorized(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    
    setTimeout(() => {
      window.location.href = '/login?from=' + encodeURIComponent(window.location.pathname);
    }, 2000);
  }

  private handleUserDisabled(): void {
    localStorage.clear();
    setTimeout(() => {
      window.location.href = '/login?error=disabled';
    }, 2000);
  }

  private handleNotInstalled(): void {
    window.location.href = '/install';
  }

  // 显示错误消息
  showError(message: string, errorCode?: string): void {
    this.setState({ error: message });
    
    // 同时显示 MessageBox 提示
    MessageBoxUtil.error(message);
    
    // 如果是特殊错误，执行特殊处理
    if (errorCode) {
      this.handleSpecialError(errorCode);
    }
  }

  // 清除错误
  clearError(): void {
    this.setState({ error: null });
  }

  // 辅助函数：格式化带占位符的字符串
  formatMessage(templateKey: string, params: Record<string, any> = {}): string {
    let message = Lang.t(templateKey);
    
    if (!message || typeof message !== 'string') {
      console.warn(`未找到语言键: ${templateKey}`);
      return templateKey;
    }
    
    // 替换所有 {key} 格式的占位符
    return message.replace(/\{(\w+)\}/g, (match, key) => {
      return params[key] !== undefined ? params[key] : match;
    });
  }

  getUserRoleFromSpace(space: Space): SpaceMemberRole | null {
    if ((space as any).currentUserRole) {
      return (space as any).currentUserRole as SpaceMemberRole;
    }
    
    if ((space as any).userRole) {
      return (space as any).userRole as SpaceMemberRole;
    }
    
    if ((space as any).role) {
      return (space as any).role as SpaceMemberRole;
    }
    
    if ((space as any).memberInfo && (space as any).memberInfo.role) {
      return (space as any).memberInfo.role as SpaceMemberRole;
    }
    
    return null;
  }

  hasUploadPermission(space: Space): boolean {
    const userRole = this.getUserRoleFromSpace(space);
    
    if (!userRole) {
      console.warn('无法获取用户在空间中的角色，假设没有上传权限:', space.uuid);
      return false;
    }
    
    return userRole === SpaceMemberRole.READ_WRITE || 
           userRole === SpaceMemberRole.ADMIN || 
           userRole === SpaceMemberRole.UPLOAD_PREVIEW;
  }

  loadUserRolesForSpaces(spaces: Space[]) {
    const spaceRoles: Record<string, SpaceMemberRole> = {};
    
    spaces.forEach(space => {
      const role = this.getUserRoleFromSpace(space);
      if (role) {
        spaceRoles[space.uuid!] = role;
      }
    });
    
    return spaceRoles;
  }

  refreshSpaceList() {
    const { spacePager } = this.state;
    
    this.clearError();
    this.setState({ loading: true });
    
    spacePager.setFilterValue('type', 'SHARED');
    spacePager.setFilterValue('orderCreateTime', 'DESC');
    spacePager.setFilterValue('requireUploadPermission', true);
    
    spacePager.httpList(
      (response: any) => {
        const allSpaces = spacePager.data;
        
        // 调试：查看返回的数据结构
        console.log('API返回的空间数据:', allSpaces);
        if (allSpaces.length > 0) {
          console.log('第一个空间对象的角色信息:', {
            uuid: allSpaces[0].uuid,
            name: allSpaces[0].name,
            currentUserRole: (allSpaces[0] as any).currentUserRole,
            userRole: (allSpaces[0] as any).userRole,
            role: (allSpaces[0] as any).role,
            memberInfo: (allSpaces[0] as any).memberInfo
          });
        }
        
        const spaceRoles = this.loadUserRolesForSpaces(allSpaces);
        
        this.setState({
          spacePager,
          spaceRoles,
          loading: false
        });
      },
      (error: any) => {
        console.error('加载空间列表失败:', error);
        
        // 处理后台返回的错误
        const errorMessage = this.handleBackendError(error);
        const errorCode = error.response?.data?.code;
        
        this.showError(errorMessage, errorCode);
        this.setState({ loading: false });
      }
    );
  }

  getSpaceRoleDescription(space: Space): string {
    const { spaceRoles, spaceMemberRoleMap } = this.state;
    const userRole = spaceRoles[space.uuid!] || this.getUserRoleFromSpace(space);
    
    if (userRole) {
      const roleOption = spaceMemberRoleMap[userRole];
      return roleOption ? roleOption.name : '未知角色';
    }
    
    return '角色未知';
  }

  getRoleColor(space: Space): string {
    const { spaceRoles, spaceMemberRoleMap } = this.state;
    const userRole = spaceRoles[space.uuid!] || this.getUserRoleFromSpace(space);
    
    if (userRole) {
      const roleOption = spaceMemberRoleMap[userRole];
      return roleOption ? roleOption.color : 'default';
    }
    
    return 'default';
  }

  handleSpaceSelectChange = (values: string[]) => {
    const { spacePager } = this.state;
    
    const selectedSpaces = spacePager.data.filter(space => 
      space.uuid && values.includes(space.uuid) // 添加 null 检查
    );
    
    this.setState({
      selectedSpaceUuids: values,
      selectedSpaces
    });
  };

  handleForward = async () => {
    const { selectedSpaceUuids } = this.state;
    const { onForward } = this.props;
    
    console.log('handleForward 开始执行', { selectedSpaceUuids, onForward });
    
    if (selectedSpaceUuids.length === 0) {
      MessageBoxUtil.warning(Lang.t('matter.selectTargetSpace') || '请先选择要转发到的空间');
      return;
    }
  
    this.setState({ forwarding: true });
    
    try {
      console.log('准备调用 onForward');
      
      if (typeof onForward === 'function') {
        await onForward(selectedSpaceUuids);
        console.log('onForward 调用完成');
      } else {
        console.error('onForward 不是函数:', onForward);
      }
    } catch (error: any) {
      console.error('捕获到错误，开始处理:', error);
      
      // 详细分析错误
      if (error?.response?.data) {
        console.log('错误包含响应数据:', error.response.data);
      }
      
      if (error?.message) {
        console.log('错误消息:', error.message);
      }
      
      const errorMessage = this.handleBackendError(error);
      const errorCode = this.getErrorCode(error);
      
      console.log('最终要显示的错误信息:', { errorMessage, errorCode });
      
      this.showError(errorMessage, errorCode);
    } finally {
      this.setState({ forwarding: false });
      console.log('handleForward 执行结束');
    }
  };

  getSpaceDisplayName = (space: Space): string => {
    return space.name || (Lang.t('space.unknownSpace') || '未知空间');
  };

  getSpaceCapacityInfo = (space: Space): string => {
    if (space.totalSizeLimit && space.totalSize) {
      return `${space.getHumanizeTotalSize()} / ${space.getHumanizeTotalSizeLimit()}`;
    }
    return Lang.t('space.capacityUnknown') || '容量未知';
  };

  renderSpaceOption = (space: Space) => {
    const roleDescription = this.getSpaceRoleDescription(space);
    const roleColor = this.getRoleColor(space);
    const hasRoleInfo = roleDescription !== '角色未知';
    
    return (
      <Option key={space.uuid} value={space.uuid}>
        <div className="space-option">
          <TeamOutlined />
          <span className="space-name ml5">{this.getSpaceDisplayName(space)}</span>
          {hasRoleInfo && (
            <span className="ml5">({roleDescription})</span>
          )}
          <span className="space-info ml10 text-gray">
            ({this.getSpaceCapacityInfo(space)})
          </span>
        </div>
      </Option>
    );
  };

  render() {
    const { visible, onClose, matterUuids } = this.props;
    const { 
      selectedSpaceUuids, 
      spacePager,
      forwarding, 
      selectedSpaces,
      error,
      loading,
      spaceMemberRoleMap
    } = this.state;

    const spaces = spacePager.data;
    const totalSpaces = spaces.length;

    return (
      <Modal
        className="forward-dialog-modal"
        title={Lang.t('matter.forwardToSpace') || '转发到空间'}
        visible={visible}
        onCancel={onClose}
        footer={[
          <Button key="cancel" onClick={onClose}>
            {Lang.t('cancel') || '取消'}
          </Button>,
          <Button
            key="forward"
            type="primary"
            loading={forwarding}
            onClick={this.handleForward}
            disabled={selectedSpaceUuids.length === 0}
          >
            {Lang.t('matter.confirmForward') || '确认转发'}
          </Button>,
        ]}
        width={700}
        destroyOnClose
      >
        <div className="forward-dialog">
          {/* 错误提示 */}
          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              onClose={() => this.clearError()}
              className="mb15"
            />
          )}

          <div className="mb20">
            <p className="text-gray mb10">
              <InfoCircleOutlined className="mr5" />
              {Lang.t('matter.forwardDescription') || '将选中的文件从您的个人空间转发到目标共享空间'}
            </p>
            <p className="text-gray">
              {this.formatMessage('matter.selectedFilesCount', { count: matterUuids.length })}
            </p>
            <Alert
              className="mt10"
              message={Lang.t('attention') || '注意'}
              description={Lang.t('matter.uploadPermissionHint') || '只能转发到您有上传权限的空间（管理员、读写、上传预览角色）'}
              type="info"
              showIcon
            />
          </div>

          <div className="space-selector">
            <div className="mb15">
              <p className="mb10">
                <TeamOutlined className="mr5" />
                {this.formatMessage('matter.selectTargetSpaceWithCount', { count: totalSpaces })}
              </p>
              
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder={Lang.t('matter.selectSpacePlaceholder') || '请搜索并选择目标空间'}
                value={selectedSpaceUuids}
                onChange={this.handleSpaceSelectChange}
                filterOption={(input, option) => {
                  const space = spaces.find(s => s.uuid === option?.key);
                  return space ? (space.name || '').toLowerCase().includes(input.toLowerCase()) : false;
                }}
                showSearch
                allowClear
                loading={loading}
                notFoundContent={
                  loading ? (
                    <div className="text-center p10">
                      <span>{Lang.t('loading') || '加载中...'}</span>
                    </div>
                  ) : (
                    <Empty 
                      description={
                        totalSpaces === 0 
                          ? (Lang.t('matter.noUploadPermissionSpaces') || '您尚未加入任何有上传权限的共享空间')
                          : (Lang.t('matter.noMatchingSpace') || '未找到匹配的空间')
                      } 
                      image={Empty.PRESENTED_IMAGE_SIMPLE} 
                    />
                  )
                }
              >
                {spaces.map(space => this.renderSpaceOption(space))}
              </Select>
              
              <div className="mt10 text-gray text-small">
                <p>{this.formatMessage('matter.loadedSpacesCount', { count: totalSpaces })}</p>
                <p className="text-xs">
                  <Tag color={spaceMemberRoleMap[SpaceMemberRole.ADMIN].color} className="ml5">
                    {spaceMemberRoleMap[SpaceMemberRole.ADMIN].name}
                  </Tag>
                  <Tag color={spaceMemberRoleMap[SpaceMemberRole.READ_WRITE].color} className="ml5">
                    {spaceMemberRoleMap[SpaceMemberRole.READ_WRITE].name}
                  </Tag>
                  <Tag color={spaceMemberRoleMap[SpaceMemberRole.UPLOAD_PREVIEW].color} className="ml5">
                    {spaceMemberRoleMap[SpaceMemberRole.UPLOAD_PREVIEW].name}
                  </Tag>
                  {Lang.t('matter.hasUploadPermission') || '角色具有上传权限'}
                </p>
              </div>
            </div>
            
            {selectedSpaces.length > 0 && (
              <div className="selected-spaces mt15">
                <p className="text-gray mb5">
                  {this.formatMessage('matter.selectedSpacesCount', { count: selectedSpaces.length })}
                </p>
                <div className="space-tags">
                  {selectedSpaces.map(space => {
                    const roleDescription = this.getSpaceRoleDescription(space);
                    const roleColor = this.getRoleColor(space);
                    const hasRoleInfo = roleDescription !== '角色未知';
                    
                    return (
                      <Tag 
                        key={space.uuid} 
                        color={roleColor}
                        closable 
                        onClose={() => {
                          this.setState({
                            selectedSpaceUuids: selectedSpaceUuids.filter(uuid => uuid !== space.uuid),
                            selectedSpaces: selectedSpaces.filter(s => s.uuid !== space.uuid)
                          });
                        }}
                      >
                        <TeamOutlined className="mr5" />
                        {this.getSpaceDisplayName(space)}
                        {hasRoleInfo && (
                          <span className="ml5">({roleDescription})</span>
                        )}
                      </Tag>
                    );
                  })}
                </div>
              </div>
            )}
            
            {selectedSpaces.length > 0 && (
              <Alert
                className="mt15"
                message={Lang.t('matter.forwardInstructions') || '转发说明'}
                description={Lang.t('matter.forwardDetails') || '文件将被复制到目标空间的根目录，保留原始文件名。如果目标空间已存在同名文件，系统会自动添加后缀以避免覆盖。'}
                type="info"
                showIcon
              />
            )}
          </div>
        </div>
      </Modal>
    );
  }
}